import { useState } from "react";
import type { Item } from "@egalregal/shared";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";
import { placementFailureNotice } from "./placementNotice";
import { placementKey, type PlacementOutcome } from "./placements";
import { usePlacementsStore } from "./placementsStore";

export interface PlacementNotice {
  message: string;
  /** Present after a removal: restore the placement at its original quantity. */
  undo?: () => Promise<void>;
}

/**
 * The list and search placement editor (placement-ledger 03): a thin adapter
 * over the shared slice.
 *
 * The slice owns the optimistic totals, the request and the one undo slot; this
 * hook only projects a row's total and translates an outcome into the shared
 * snackbar. A removal still offers Undo, which now restores the placement at
 * the quantity it had before it was removed.
 *
 * @param refresh called after a saved removal or undo so the caller re-reads
 *   the mirror (item list) or re-runs the search and drops/restores the row.
 */
export function usePlacementQuantity(refresh: () => void) {
  const t = useT();
  const optimistic = usePlacementsStore((state) => state.optimistic);
  const setTotal = usePlacementsStore((state) => state.setTotal);
  const remove = usePlacementsStore((state) => state.remove);
  const undoLast = usePlacementsStore((state) => state.undoLast);
  const [notice, setNotice] = useState<PlacementNotice | null>(null);

  /** The row total: the slice's projection, else the stored quantity. */
  const quantityOf = (containerId: string, itemId: string, stored: number | undefined) =>
    optimistic[placementKey(containerId, itemId)] ?? stored;

  const fail = (outcome: PlacementOutcome, itemId: string, containerId: string) => {
    if (outcome.status === "saved") return;
    log.warn({ outcome, itemId, containerId }, "Placement quantity change failed");
    setNotice({ message: placementFailureNotice(t, outcome) });
  };

  const change = async ({
    item,
    containerId,
    next,
  }: {
    item: Item;
    containerId: string;
    next: number;
  }): Promise<PlacementOutcome> => {
    if (next < 0) return { status: "saved", quantity: 0 };
    const outcome =
      next === 0 ? await remove(containerId, item) : await setTotal(containerId, item, next);
    if (outcome.status !== "saved") {
      fail(outcome, item.id, containerId);
      return outcome;
    }
    if (next === 0) {
      // A saved removal keeps its own snackbar; the slice's slot restores the
      // placement at the exact quantity it was removed from.
      setNotice({
        message: t("placementRemoved", { name: item.name }),
        undo: async () => {
          const restored = await undoLast();
          if (restored.status !== "saved") {
            fail(restored, item.id, containerId);
            return;
          }
          setNotice(null);
          refresh();
        },
      });
      refresh();
    }
    return outcome;
  };

  const undo = async () => {
    const action = notice?.undo;
    if (!action) return;
    setNotice(null);
    // The action owns the restore; a failure restores the slot and shows the
    // same notice the original write would have shown.
    await action();
  };

  return {
    notice,
    dismiss: () => setNotice(null),
    quantityOf,
    change,
    undo,
  };
}
